// src/services/challengeService.js

import {
  collection,
  orderBy,
  limit,
  getDocs,
  startAfter,
  getFirestore,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  onSnapshot,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";

export const fetchCreatorProfile = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;

  const db = getFirestore();
  try {
    const userProfileDoc = await getDoc(doc(db, "users", user.uid));
    if (userProfileDoc.exists()) {
      const profileData = userProfileDoc.data();
      return {
        creatorUid: user.uid,
        displayName: profileData.displayName || "Anonymous",
        userName: profileData.userName || "Anonymous",
        profileImage: profileData.profileImage || "",
      };
    } else {
      console.warn("No such user profile!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const fetchChallenge = async (id) => {
  try {
    const docRef = doc(db, "challenges", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }; // Return the challenge data with ID
    } else {
      throw new Error("No such document!");
    }
  } catch (error) {
    console.error("Error fetching challenge:", error);
    throw error; // Re-throw the error to handle it in the component
  }
};

export const fetchComments = (challengeId, setComments, setLoadingComments) => {
  const commentsRef = collection(db, "challenges", challengeId, "comments");
  const q = query(commentsRef);

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedComments = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setComments(fetchedComments);
    setLoadingComments(false);
  });

  return unsubscribe;
};

export const addComment = async (challengeId, newComment, user) => {
  try {
    const commentsRef = collection(db, "challenges", challengeId, "comments");
    await addDoc(commentsRef, {
      text: newComment,
      createdAt: serverTimestamp(),
      createdBy: user.displayName || "Anonymous",
      userId: user.uid,
    });
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Fetch submissions based on challengeId, sorting, and pagination options
export const fetchSubmissions = async (
  challengeId,
  { limitNumber = 0, sortByDate = "latest", lastVisibleDoc = null } = {}
) => {
  try {
    // Reference to the root-level submissions collection in Firestore
    const submissionsRef = collection(db, "submissions");

    // Use "createdAt" as the field to order by
    const sortField = "createdAt";
    const sortDirection = sortByDate === "latest" ? "desc" : "asc";

    // Create a query based on the sort direction and limit if provided
    let queryConstraints = [
      where("challengeId", "==", challengeId), // Filter by challengeId
      where("status", "==", "published"), // Filter by published status
      orderBy(sortField, sortDirection), // Sort by createdAt field
    ];

    if (limitNumber > 0) {
      queryConstraints.push(limit(limitNumber));
    }

    // Add pagination support with startAfter if lastVisibleDoc is provided
    if (lastVisibleDoc) {
      queryConstraints.push(startAfter(lastVisibleDoc));
    }

    // Perform the query
    const q = query(submissionsRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    // Map the results to a usable format
    const submissions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Get the last visible document for pagination
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

    // console.log("Fetched submissions from Firestore:", submissions);

    // Return the fetched submissions and the last visible document for pagination
    return { submissions, lastVisible };
  } catch (error) {
    console.error("Error fetching submissions from Firestore:", error);
    throw error;
  }
};

export const fetchHomePageChallenges = async () => {
  try {
    const challengesRef = collection(db, "challenges");
    const publishedChallengesQuery = query(
      challengesRef,
      where("published", "==", true),
      orderBy("startDate", "desc") // Sort by startDate in descending order
    );
    const snapshot = await getDocs(publishedChallengesQuery);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching home page challenges:", error);
    throw error; // Re-throw the error to handle it in the component
  }
};

// Function to create a challenge in Firestore
export const createChallengeInFirestore = async (challengeData) => {
  try {
    const db = getFirestore();
    const docRef = await addDoc(collection(db, "challenges"), challengeData);
    return docRef.id; // Return the generated document ID
  } catch (error) {
    throw new Error(
      `Failed to create challenge in Firestore: ${error.message}`
    );
  }
};

// Function to get a challenge by its ID from Firestore
export const getChallengeById = async (challengeId) => {
  try {
    const db = getFirestore();
    const challengeRef = doc(db, "challenges", challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (challengeSnap.exists()) {
      return { id: challengeSnap.id, ...challengeSnap.data() };
    } else {
      throw new Error("No challenge found with the given ID");
    }
  } catch (error) {
    console.error("Error fetching challenge by ID:", error);
    throw new Error(`Failed to fetch challenge: ${error.message}`);
  }
};

// Function to update an existing challenge in Firestore
export const updateChallengeInFirestore = async (challengeId, updatedData) => {
  if (!challengeId) {
    throw new Error("Challenge ID is not defined");
  }

  if (!updatedData || typeof updatedData !== "object") {
    throw new Error("Updated data is not a valid object");
  }

  try {
    const db = getFirestore();
    const challengeRef = doc(db, "challenges", challengeId);
    await updateDoc(challengeRef, updatedData);
    return true; // Return success
  } catch (error) {
    throw new Error(
      `Failed to update challenge in Firestore: ${error.message}`
    );
  }
};

// Function to fetch a submission by its Firestore ID
export const fetchChallengeById = async (challengeId) => {
  if (!challengeId) {
    throw new Error("Challenge ID is required");
  }

  try {
    const challengeRef = doc(db, "challenges", challengeId);
    const challengeSnapshot = await getDoc(challengeRef);

    if (challengeSnapshot.exists()) {
      return { id: challengeSnapshot.id, ...challengeSnapshot.data() };
    } else {
      throw new Error("Challenge not found");
    }
  } catch (error) {
    console.error("Error fetching challenge:", error);
    throw error;
  }
};
